import type { PhoneCallView } from '../../../queries/PhoneCall/usePhoneCallFilterQuery';
import { formatCallTimestamp } from './callDisplay';
import { getCallSubject } from './leadLabel';
import { isScorable } from './repCardFilters';

/**
 * What a rep card knew about its calls the last time it looked: the score each call carried (`null`
 * when it wasn't scorable yet) and how recent the newest call was.
 *
 * Both halves are needed because a coaching score lands *after* the call does — a row first appears
 * unscored and is scored minutes later, so "have I seen this id?" alone would miss every real alert,
 * and "is it scored?" alone would alert for the whole backlog on first load.
 */
export interface AlertBaseline {
	scores: Map<string, number | null>;
	latestCallMs: number;
}

const callTimeMs = (view: PhoneCallView): number => {
	const parsed = Date.parse(view.phoneCall.creationDate ?? '');
	return Number.isNaN(parsed) ? 0 : parsed;
};

const scoreOf = (view: PhoneCallView): number | null => {
	const score = view.phoneCall.coachingAdherenceResult?.score;
	return isScorable(score) ? (score as number) : null;
};

const newestCallMs = (calls: PhoneCallView[]): number =>
	calls.reduce((newest, view) => Math.max(newest, callTimeMs(view)), 0);

export const createBaseline = (calls: PhoneCallView[]): AlertBaseline => ({
	scores: new Map(calls.filter(view => view.phoneCall.id).map(view => [view.phoneCall.id as string, scoreOf(view)])),
	latestCallMs: newestCallMs(calls),
});

export const advanceBaseline = (baseline: AlertBaseline, calls: PhoneCallView[]): AlertBaseline => {
	const scores = new Map(baseline.scores);
	calls.forEach(view => {
		if (view.phoneCall.id) {
			scores.set(view.phoneCall.id, scoreOf(view));
		}
	});
	return { scores, latestCallMs: Math.max(baseline.latestCallMs, newestCallMs(calls)) };
};

/**
 * Calls worth alerting on: either a call newer than anything the baseline had, or one the baseline
 * held unscored that has since been scored. A `Load more` page of older calls is neither, so paging
 * back through the history never fires an alert.
 */
export function findNewHighScoreCalls(
	calls: PhoneCallView[],
	baseline: AlertBaseline,
	threshold: number
): PhoneCallView[] {
	return calls.filter(view => {
		const id = view.phoneCall.id;
		if (!id) {
			return false;
		}
		const score = scoreOf(view);
		if (score == null || score < threshold) {
			return false;
		}
		if (!baseline.scores.has(id)) {
			return callTimeMs(view) > baseline.latestCallMs;
		}
		return baseline.scores.get(id) == null;
	});
}

export interface HighScoreAlert {
	title: string;
	body: string;
	/** Absent when the top call has no linked conversation — the alert then has nothing to open. */
	conversationId?: string;
	/** Dedupe key for the OS notification center so a repeat delivery replaces rather than stacks. */
	tag: string;
}

export function buildHighScoreAlert(repName: string, calls: PhoneCallView[], threshold: number): HighScoreAlert | null {
	if (calls.length === 0) {
		return null;
	}

	const ranked = [...calls].sort((a, b) => (scoreOf(b) ?? 0) - (scoreOf(a) ?? 0));
	const top = ranked[0];
	const topScore = scoreOf(top) ?? 0;
	const conversationId = top.phoneCall.aiConversationId || undefined;
	const tag = `ai-coaching-review:${top.phoneCall.id}`;

	if (ranked.length === 1) {
		return {
			title: `${repName} scored ${topScore}`,
			body: `${getCallSubject(top)} · ${formatCallTimestamp(top.phoneCall.creationDate)}`,
			conversationId,
			tag,
		};
	}

	return {
		title: `${repName}: ${ranked.length} calls scored ${threshold}+`,
		body: `Top score ${topScore} · ${getCallSubject(top)}`,
		conversationId,
		tag,
	};
}
